"use client";

import { useEffect, useState } from "react";
import { PropertyTypeV2 } from "commonModule/services/common";
import useCurrency from "hooks/swr/useCurrency";
import { markBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch } from "stores/hooks";
import BookingActions from "./blocks/BookingActions";
import BookingPriceHeader from "./blocks/BookingPriceHeader";
import ClientViewBlock from "./blocks/ClientViewBlock";
import CommissionRateModal from "./blocks/CommissionRateModal";
import DateGuestSelector from "./blocks/DateGuestSelector";
import PriceBreakdownBlock from "./blocks/PriceBreakdownBlock";
import { useBookingSummary } from "./blocks/useBookingSummary";
import { BookingPriceLoading } from "./BookingPriceLoading";
import NotLoggedInBookingState from "./NotLoggedInBookingState";
import { useBookingSectionController } from "./useBookingSectionController";
const BookingCardDesktop = ({
  property
}: {
  property: PropertyTypeV2;
}) => {
  const dispatch = useAppDispatch();
  const {
    formatter,
    rate: fxRate
  } = useCurrency();
  const {
    bookingStore,
    isLoggedIn,
    isAgent,
    isTravelAgentClient,
    isAuthPending,
    loaderDots,
    translateBookBtn,
    onBookClick,
    openCalendar
  } = useBookingSectionController({
    property
  });
  const {
    derivedCommissionRate
  } = useBookingSummary();
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Resolved view type — evaluated once, used everywhere
  const showLoginState = !isLoggedIn && !isTravelAgentClient;
  const showNotEligibleState = isLoggedIn && !isAgent && !isTravelAgentClient;
  const showClientView = isTravelAgentClient && !isAgent;
  // showAgentView = isAgent (logged in implied by useIsAgent)

  const canShowBreakdown = bookingStore.hasPrequote && !!bookingStore.startDate && !!bookingStore.endDate && bookingStore.isDateAvailable;
  return <>
      <div className='flex w-full flex-col rounded-[8px] bg-white p-6 shadow-[0_4px_20px_0_rgba(55,56,77,0.13)]'>
        {(!isMounted || isAuthPending) && <BookingPriceLoading />}

        {isMounted && !isAuthPending && showLoginState && <NotLoggedInBookingState variant='login' />}
        {isMounted && !isAuthPending && showNotEligibleState && <NotLoggedInBookingState variant='not-eligible' />}

        {isMounted && !isAuthPending && (isAgent || showClientView) && bookingStore?.isLoading && <BookingPriceLoading />}

        {isMounted && !isAuthPending && (isAgent || showClientView) && !bookingStore?.isLoading && !canShowBreakdown && <div className='flex flex-col gap-4'>
              <p className='text-[22px] font-bold leading-none text-textColor'>
                {formatter(0, 0).format((property?.min_price || 0) * fxRate)}
                <span className='ml-1 text-[14px] font-medium text-Gray-700'>
                  avg/night
                </span>
              </p>
              <DateGuestSelector property={property} onDateClick={openCalendar} isDisplayClearDates={true} />
              <BookingActions propertyId={property.property_id} bookLabel={translateBookBtn} loaderDots={loaderDots} onBookClick={onBookClick} layout='stacked' showShare={false} />
            </div>}

        {isMounted && !isAuthPending && (isAgent || showClientView) && !bookingStore?.isLoading && canShowBreakdown && <div className='flex flex-col gap-4'>
              <BookingPriceHeader size='card' isTravelAgentClient={showClientView} dateGuestSelector={<DateGuestSelector property={property} onDateClick={openCalendar} isDisplayClearDates={true} />} />
              {isAgent && <>
                  <div className='h-[1px] w-full bg-Gray-200' />
                  <PriceBreakdownBlock onChangeRate={() => setIsRateModalOpen(true)} />
                  <div className='h-[1px] w-full bg-Gray-200' />
                  <ClientViewBlock />
                </>}
              {showClientView && <div className='h-[1px] w-full bg-Gray-200' />}
              <BookingActions propertyId={property.property_id} city={property.city} country={property.country} bookLabel='Book now' loaderDots={loaderDots} onBookClick={onBookClick} layout='stacked' showShare={isAgent} />
              {showClientView && <p className='text-center text-[13px] font-medium leading-none tracking-[-0.13px] text-Gray-800'>
                  You won&apos;t be charged yet
                </p>}
            </div>}
      </div>
      {isAgent && <CommissionRateModal isOpen={isRateModalOpen} currentRate={derivedCommissionRate} onClose={() => setIsRateModalOpen(false)} onConfirm={next => {
      dispatch(markBookingStore({
        commissionRatePercent: next
      }));
      setIsRateModalOpen(false);
    }} />}
    </>;
};
export default BookingCardDesktop;